import z from 'zod';
import { crateItem } from '../crate';

const shipmentStatus = z.enum(['DRAFT', 'LOADED', 'SHIPPED', 'DELIVERED']);
const decimalLike = z.union([z.string(), z.number()]).nullable();
const emptyToUndefined = (value: unknown) =>
  value === '' || value === null ? undefined : value;
const optionalText = z.preprocess(
  emptyToUndefined,
  z.string().trim().optional().nullable(),
);
const optionalRelease = z.preprocess(
  emptyToUndefined,
  z.coerce.number().positive('Release must be valid').optional().nullable(),
);
const optionalDate = z.preprocess(
  emptyToUndefined,
  z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .optional()
    .nullable(),
);
const shipmentFormBodyBase = z.object({
  name: optionalText,
  release: optionalRelease,
  truck_number: optionalText,
  carrier: optionalText,
  driver: optionalText,
  ship_date: optionalDate,
  delivery_date: optionalDate,
  destination: optionalText,
  notes: optionalText,
});
const shipmentDateOrderRefinement = (
  body: z.infer<typeof shipmentFormBodyBase>,
) =>
  !body.ship_date ||
  !body.delivery_date ||
  body.delivery_date >= body.ship_date;
const shipmentFormBody = shipmentFormBodyBase.refine(
  shipmentDateOrderRefinement,
  {
    path: ['delivery_date'],
    message: 'Delivery date cannot be before ship date',
  },
);

const shipmentBase = {
  id: z.number(),
  project: z.number(),
  release: z.number().nullable(),
  shipment_number: z.string().nullable(),
  name: z.string().nullable(),
  truck_number: z.string().nullable(),
  carrier: z.string().nullable(),
  driver: z.string().nullable(),
  ship_date: z.string().nullable(),
  delivery_date: z.string().nullable(),
  destination: z.string().nullable(),
  status: shipmentStatus,
  notes: z.string().nullable(),
  item_count: z.number(),
  created_at: z.string(),
};
const shipmentObject = z.object(shipmentBase);

const shipmentItem = z.object({
  id: z.number(),
  item_type: z.enum(['CRATE', 'FRAME', 'PART', 'MISC']),
  crate: z.number().nullable(),
  crate_number: z.string().nullable().optional(),
  crate_status: z.string().nullable().optional(),
  crate_name: z.string().nullable().optional(),
  crate_description: z.string().nullable().optional(),
  crate_dimensions: z.string().nullable().optional(),
  crate_weight: z.string().nullable().optional(),
  crate_item_count: z.number().optional(),
  crate_total_quantity: z.number().optional(),
  crate_items: z.array(crateItem).optional().default([]),
  frame: z.number().nullable(),
  frame_name: z.string().nullable().optional(),
  part: z.number().nullable(),
  part_number: z.string().nullable().optional(),
  quantity: z.number(),
  notes: z.string().nullable(),
});

const shipmentWithItems = z.object({
  id: z.number(),
  project: z.number(),
  project_name: z.string().nullable().optional(),
  release: z.number().nullable(),
  release_number: z.string().nullable().optional(),
  shipment_number: z.string().nullable(),
  name: z.string().nullable(),
  truck_number: z.string().nullable(),
  carrier: z.string().nullable(),
  driver: z.string().nullable(),
  ship_date: z.string().nullable(),
  delivery_date: z.string().nullable(),
  destination: z.string().nullable(),
  status: shipmentStatus,
  notes: z.string().nullable(),
  created_at: z.string(),
  items: z.array(shipmentItem),
});

const crateSummary = z.object({
  id: z.number(),
  project: z.number(),
  release: z.number().nullable(),
  crate_number: z.string().nullable(),
  name: z.string().nullable(),
  description: z.string().nullable(),
  length: decimalLike,
  width: decimalLike,
  height: decimalLike,
  dimension_unit: z.string().nullable(),
  weight: decimalLike,
  weight_unit: z.string().nullable(),
  status: z.string(),
  item_count: z.number(),
  created_at: z.string(),
});

// ---- Create shipment ----
export const postShipment = {
  body: shipmentFormBody,
  response: shipmentObject,
  params: z.object({ project_id: z.coerce.number() }),
  query: z.never(),
};
const postShipmentSchema = z.object(postShipment);
export type PostShipment = z.infer<typeof postShipmentSchema>;

// ---- List shipments ----
export const getShipments = {
  body: z.never(),
  response: z.array(shipmentObject),
  params: z.object({ project_id: z.coerce.number() }),
  query: z.object({ status: shipmentStatus.optional() }).optional(),
};
const getShipmentsSchema = z.object(getShipments);
export type GetShipments = z.infer<typeof getShipmentsSchema>;

// ---- Available crates for loading ----
export const getAvailableCrates = {
  body: z.never(),
  response: z.array(crateSummary),
  params: z.object({ project_id: z.coerce.number() }),
  query: z.object({ release: z.coerce.number().optional() }).optional(),
};
const getAvailableCratesSchema = z.object(getAvailableCrates);
export type GetAvailableCrates = z.infer<typeof getAvailableCratesSchema>;

// ---- Shipment detail ----
export const getShipment = {
  body: z.never(),
  response: shipmentWithItems,
  params: z.object({ shipment_id: z.coerce.number() }),
  query: z.never(),
};
const getShipmentSchema = z.object(getShipment);
export type GetShipment = z.infer<typeof getShipmentSchema>;

// ---- Update shipment ----
export const patchShipment = {
  body: shipmentFormBodyBase
    .extend({
      status: shipmentStatus.optional(),
    })
    .refine(shipmentDateOrderRefinement, {
      path: ['delivery_date'],
      message: 'Delivery date cannot be before ship date',
    }),
  response: shipmentObject,
  params: z.object({ shipment_id: z.coerce.number() }),
  query: z.never(),
};
const patchShipmentSchema = z.object(patchShipment);
export type PatchShipment = z.infer<typeof patchShipmentSchema>;

// ---- Delete shipment ----
export const deleteShipment = {
  body: z.never(),
  response: z.never(),
  params: z.object({ shipment_id: z.coerce.number() }),
  query: z.never(),
};
const deleteShipmentSchema = z.object(deleteShipment);
export type DeleteShipment = z.infer<typeof deleteShipmentSchema>;

// ---- Add items (crates + loose) ----
export const postShipmentItems = {
  body: z.object({
    crate_ids: z.array(z.coerce.number()).optional(),
    loose_items: z
      .array(
        z.object({
          item_type: z.enum(['FRAME', 'PART']),
          frame_id: z.coerce.number().optional(),
          part_id: z.coerce.number().optional(),
          quantity: z.coerce.number().min(1).default(1),
          notes: z.string().optional().nullable(),
        }),
      )
      .optional(),
  }),
  response: shipmentWithItems,
  params: z.object({ shipment_id: z.coerce.number() }),
  query: z.never(),
};
const postShipmentItemsSchema = z.object(postShipmentItems);
export type PostShipmentItems = z.infer<typeof postShipmentItemsSchema>;

// ---- Remove items ----
export const deleteShipmentItems = {
  body: z.object({ item_ids: z.array(z.coerce.number()).min(1) }),
  response: z.object({ removed: z.array(z.number()) }),
  params: z.object({ shipment_id: z.coerce.number() }),
  query: z.never(),
};
const deleteShipmentItemsSchema = z.object(deleteShipmentItems);
export type DeleteShipmentItems = z.infer<typeof deleteShipmentItemsSchema>;

// ---- Export shipment sheet (blob) ----
export const exportShipmentSheet = {
  body: z.object({}).optional(),
  response: z.any(),
  params: z.object({ shipment_id: z.coerce.number() }),
  query: z.never(),
};
const exportShipmentSheetSchema = z.object(exportShipmentSheet);
export type ExportShipmentSheet = z.infer<typeof exportShipmentSheetSchema>;
